import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Users, Plus, Edit, Trash2, Save, X, Upload, Mail, Phone} from 'lucide-react'
import toast from "react-hot-toast"

const POSITION_ORDER: Record<string, number> = {
  "President": 1,
  "Vice President": 2,
  "Treasurer": 3,
  "Secretary": 4,
  "General Secretary": 5,
  "Organizing Secretary": 6,
  "Welfare Secretary": 7
}

const getPositionOrder = (position: string): number => {
  const normalized = position?.trim() || ""
  for (const [key, order] of Object.entries(POSITION_ORDER)) {
    if (normalized.toLowerCase() === key.toLowerCase()) return order
  }
  return 99
}

function Executives() {
  const [user, setUser] = useState(lumi.auth.user)
  const [executives, setExecutives] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingExecutive, setEditingExecutive] = useState<any>(null)
  const [uploading, setUploading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    position: "",
    region: "Ghana",
    bio: "",
    email: "",
    phone: "",
    imageUrl: "",
    order: 0
  })

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    loadExecutives()
  }, [])

  const loadExecutives = async () => {
    try {
      const result = await lumi.entities.executives.list({
        sort: { region: 1, order: 1 }
      })
      const sorted = [...result.list].sort((a, b) => {
        if (a.region !== b.region) return (a.region || "").localeCompare(b.region || "")
        return getPositionOrder(a.position) - getPositionOrder(b.position)
      })
      setExecutives(sorted)
    } catch (error) {
      console.error("Failed to load executives:", error)
      toast.error("Failed to load executives")
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const value = e.target.type === "number" ? Number(e.target.value) : e.target.value
    setFormData({ ...formData, [e.target.name]: value })
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      const result = await lumi.tools.file.upload(Array.from(files))
      if (result[0]?.fileUrl) {
        setFormData({ ...formData, imageUrl: result[0].fileUrl })
        toast.success("Image uploaded!")
      } else {
        toast.error("Image upload failed")
      }
    } catch (error) {
      console.error("Upload failed:", error)
      toast.error("Failed to upload image")
    } finally {
      setUploading(false)
    }
  }

  const openAddModal = () => {
    setEditingExecutive(null)
    setFormData({
      name: "",
      position: "",
      region: "Ghana",
      bio: "",
      email: "",
      phone: "",
      imageUrl: "",
      order: 0
    })
    setShowModal(true)
  }

  const openEditModal = (executive: any) => {
    setEditingExecutive(executive)
    setFormData({
      name: executive.name || "",
      position: executive.position || "",
      region: executive.region || "Ghana",
      bio: executive.bio || "",
      email: executive.email || "",
      phone: executive.phone || "",
      imageUrl: executive.imageUrl || "",
      order: executive.order || 0
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!formData.name || !formData.position || !formData.region) {
      toast.error("Please fill in all required fields")
      return
    }

    try {
      const data = {
        ...formData,
        updatedAt: new Date()
      }

      if (editingExecutive) {
        await lumi.entities.executives.update(editingExecutive._id, data)
        toast.success("Executive updated!")
      } else {
        await lumi.entities.executives.create({
          ...data,
          createdAt: new Date()
        })
        toast.success("Executive added!")
      }

      setShowModal(false)
      loadExecutives()
    } catch (error) {
      console.error("Save failed:", error)
      toast.error("Failed to save executive")
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this executive?")) return

    try {
      await lumi.entities.executives.delete(id)
      toast.success("Executive deleted!")
      loadExecutives()
    } catch (error) {
      console.error("Delete failed:", error)
      toast.error("Failed to delete executive")
    }
  }

  const groupedExecutives = {
    "Ghana": executives.filter(e => e.region === "Ghana"),
    "Europe": executives.filter(e => e.region === "Europe"),
    "North America": executives.filter(e => e.region === "North America")
  }

  if (loading) {
    return (
      <div className="min-h-screen py-16 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading executives...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-black">MOBA91 Executives</h1>
          <p className="text-gray-600 text-lg">Our Leadership Team Across the Globe</p>
        </div>

        {/* Admin Add Button */}
        {user?.userRole === "ADMIN" && (
          <div className="flex justify-end mb-8">
            <button
              onClick={openAddModal}
              className="px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center space-x-2"
            >
              <Plus size={20} />
              <span>Add Executive</span>
            </button>
          </div>
        )}

        {/* Executives by Region */}
        {Object.entries(groupedExecutives).map(([region, regionExecutives]) => (
          <div key={region} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-[#DC143C] text-center">{region} Executives</h2>
            
            {regionExecutives.length === 0 ? (
              <div className="text-center py-12 bg-white border-2 border-[#DC143C]/30 rounded-2xl">
                <Users size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No executives added yet for {region}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regionExecutives.map((executive) => (
                  <div
                    key={executive._id}
                    className="bg-white border-2 border-[#DC143C]/30 rounded-2xl overflow-hidden hover:border-[#DC143C] transition-all shadow-lg hover:shadow-xl"
                  >
                    {/* Image Placeholder */}
                    <div className="relative h-64 bg-gray-100 overflow-hidden">
                      {executive.imageUrl ? (
                        <img
                          src={executive.imageUrl}
                          alt={executive.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-24 h-24 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                              <Users size={48} className="text-white" />
                            </div>
                            <p className="text-gray-500 text-sm font-semibold">Executive Photo</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-1 text-black">{executive.name}</h3>
                      <p className="text-[#DC143C] font-semibold mb-4">{executive.position}</p>
                      
                      {executive.bio && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{executive.bio}</p>
                      )}

                      {/* Contact Info */}
                      <div className="space-y-2 mb-4">
                        {executive.email && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail size={16} className="text-[#DC143C]" />
                            <span className="truncate">{executive.email}</span>
                          </div>
                        )}
                        {executive.phone && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Phone size={16} className="text-[#DC143C]" />
                            <span>{executive.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Admin Controls */}
                      {user?.userRole === "ADMIN" && (
                        <div className="flex space-x-2 pt-4 border-t border-[#DC143C]/20">
                          <button
                            onClick={() => openEditModal(executive)}
                            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-[#DC143C] hover:text-white text-black rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                          >
                            <Edit size={16} />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(executive._id)}
                            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-red-600 hover:text-white text-black rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                          >
                            <Trash2 size={16} />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-white border-2 border-[#DC143C]/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-black">
                    {editingExecutive ? "Edit Executive" : "Add Executive"}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photo <span className="text-gray-500">(optional)</span>
                    </label>
                    <div className="flex items-center space-x-4">
                      {formData.imageUrl && (
                        <img
                          src={formData.imageUrl}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-lg border border-[#DC143C]/30"
                        />
                      )}
                      <label className="flex-1 cursor-pointer">
                        <div className="px-4 py-3 bg-gray-50 border border-[#DC143C]/30 rounded-lg text-center hover:border-[#DC143C] transition-all">
                          <Upload size={20} className="inline mr-2" />
                          <span>{uploading ? "Uploading..." : "Upload Photo"}</span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name <span className="text-[#DC143C]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position <span className="text-[#DC143C]">*</span>
                      </label>
                      <select
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                      >
                        <option value="">Select Position</option>
                        <option value="President">President</option>
                        <option value="Vice President">Vice President</option>
                        <option value="Treasurer">Treasurer</option>
                        <option value="Secretary">Secretary</option>
                        <option value="General Secretary">General Secretary</option>
                        <option value="Organizing Secretary">Organizing Secretary</option>
                        <option value="Welfare Secretary">Welfare Secretary</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region <span className="text-[#DC143C]">*</span>
                      </label>
                      <select
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                      >
                        <option value="Ghana">Ghana</option>
                        <option value="Europe">Europe</option>
                        <option value="North America">North America</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Display Order
                      </label>
                      <input
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Biography</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-white border border-[#DC143C]/30 rounded-lg text-black focus:border-[#DC143C] focus:outline-none"
                    ></textarea>
                  </div>
                </div>

                <div className="flex space-x-4 mt-8">
                  <button
                    onClick={handleSave}
                    className="flex-1 px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                  >
                    <Save size={20} />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-black rounded-lg font-semibold transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Executives